import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    FaFileInvoiceDollar,
    FaSync,
    FaDownload,
    FaSearch,
    FaCheckCircle,
    FaExclamationTriangle,
    FaUsers,
    FaLayerGroup,
    FaSpinner,
    FaBuilding,
    FaInfoCircle,
    FaChevronDown,
    FaChevronUp,
    FaClipboardCheck,
    FaUserCheck,
    FaFilter,
} from 'react-icons/fa';
import { BLEEP_SERVER_NAMES } from '../../utils/constants';
import {
    getBleepMonthlyLicenceReport,
    getBleepMonthlyLicenceRows,
    initializeBleepLicenceHistory,
    previewBleepLicenceIdentityMapping,
} from '../../services/onboardingService';

// Formula injection protection for CSV cells
const sanitizeCsvCell = (val) => {
    let s = String(val ?? '');
    if (/^[\s\u0000-\u001f]*[=+@-]/.test(s) || s.startsWith('\t') || s.startsWith('\r') || s.startsWith('\n')) {
        s = `'${s}`;
    }
    return `"${s.replace(/"/g, '""')}"`;
};

const MonthlyLicenceSection = ({ serverID, endDate = '', defaultCombined = false }) => {
    const [combined, setCombined] = useState(defaultCombined);
    const [busy, setBusy] = useState(false);
    const [initializing, setInitializing] = useState(false);
    const [report, setReport] = useState(null);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 20;

    // Expandable Sections
    const [openCoverageDetails, setOpenCoverageDetails] = useState(false);
    const [openCurrentAccounts, setOpenCurrentAccounts] = useState(false);

    // Current Accounts Explorer State
    const [currentRows, setCurrentRows] = useState(null);
    const [currentBusy, setCurrentBusy] = useState(false);
    const [currentError, setCurrentError] = useState(null);
    const [currentLoaded, setCurrentLoaded] = useState(false);
    const [currentQuery, setCurrentQuery] = useState('');
    const [currentAllDivisions, setCurrentAllDivisions] = useState(false);
    const [currentWithNotesOnly, setCurrentWithNotesOnly] = useState(false);
    const [currentShowTests, setCurrentShowTests] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const CURRENT_PAGE_SIZE = 12;

    const loadCurrentAccounts = useCallback(async () => {
        if (currentBusy) return;
        setCurrentBusy(true);
        setCurrentError(null);
        try {
            const res = await previewBleepLicenceIdentityMapping();
            if (res?.success !== true || !Array.isArray(res?.records)) {
                throw new Error(res?.note || 'Failed to retrieve current accounts identity mapping.');
            }
            setCurrentRows(res.records);
            setCurrentLoaded(true);
            setCurrentPage(0);
        } catch (err) {
            console.error('Failed to load current accounts:', err);
            setCurrentError(err?.message || 'Failed to load current accounts.');
        } finally {
            setCurrentBusy(false);
        }
    }, [currentBusy]);

    const loadData = useCallback(async (doInit = false) => {
        setBusy(true);
        setError(null);

        try {
            if (doInit) {
                setInitializing(true);
                await initializeBleepLicenceHistory(serverID, combined, endDate);
                setInitializing(false);
            }

            const reportRes = await getBleepMonthlyLicenceReport(serverID, combined, endDate);
            setReport(reportRes);

            // Fetch all row details (accounts, issues, changes) with pagination
            if (reportRes?.checksum) {
                const collectedRows = [];
                let cursor = '';
                const seenRowIDs = new Set();
                let hasMore = true;

                while (hasMore) {
                    const rowsRes = await getBleepMonthlyLicenceRows({
                        serverID,
                        combined,
                        endDate,
                        checksum: reportRes.checksum,
                        afterRowID: cursor,
                    });

                    if (rowsRes?.rows && Array.isArray(rowsRes.rows)) {
                        for (const row of rowsRes.rows) {
                            const rowId = row.accountKey || row.id || JSON.stringify(row);
                            if (!seenRowIDs.has(rowId)) {
                                seenRowIDs.add(rowId);
                                collectedRows.push(row);
                            }
                        }
                    }

                    if (rowsRes?.hasMore === true && rowsRes?.nextRowID && rowsRes.nextRowID !== cursor) {
                        cursor = rowsRes.nextRowID;
                    } else {
                        hasMore = false;
                    }
                }
                setRows(collectedRows.length > 0 ? collectedRows : (reportRes?.accounts || []));
            } else {
                setRows(reportRes?.accounts || []);
            }
            setPage(0);
        } catch (err) {
            console.error('Failed to load monthly licence report:', err);
            setError(err?.message || 'Failed to generate monthly licence audit report.');
        } finally {
            setBusy(false);
            setInitializing(false);
        }
    }, [serverID, combined, endDate]);

    useEffect(() => {
        loadData(false);
    }, [loadData]);

    // Accounts list with single-human deduplication
    const accountRows = useMemo(() => {
        return rows.filter((r) => r.kind === 'ACCOUNT' || (!r.kind && r.accountKey));
    }, [rows]);

    const dataIssues = useMemo(() => {
        return rows.filter((r) => r.kind === 'DATA_ISSUE') || report?.issues || [];
    }, [rows, report]);

    // Filter accounts by search & role
    const filteredAccounts = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return accountRows.filter((acc) => {
            if (roleFilter !== 'ALL') {
                const roles = acc.roles || [];
                if (!roles.includes(roleFilter)) return false;
            }
            if (q) {
                const name = (acc.name || '').toLowerCase();
                const names = Array.isArray(acc.names) ? acc.names.join(' ').toLowerCase() : '';
                const divisions = Array.isArray(acc.divisions) ? acc.divisions.join(' ').toLowerCase() : '';
                const searchable = `${name} ${names} ${divisions}`;
                if (!searchable.includes(q)) return false;
            }
            return true;
        });
    }, [accountRows, roleFilter, searchQuery]);

    // Paginated accounts slice
    const paginatedAccounts = useMemo(() => {
        const start = page * PAGE_SIZE;
        return filteredAccounts.slice(start, start + PAGE_SIZE);
    }, [filteredAccounts, page]);

    const totalPages = Math.ceil(filteredAccounts.length / PAGE_SIZE) || 1;

    // Coverage details logic
    const coverageList = useMemo(() => {
        return Array.isArray(report?.coverage) ? report.coverage : [];
    }, [report]);

    const coverageGaps = useMemo(() => {
        return coverageList.filter((c) => c.complete !== true);
    }, [coverageList]);

    const isReportComplete = report?.state === 'VERIFIED_RECORDED_HISTORY' && typeof report?.chargeableAccounts === 'number';

    // Current Accounts Explorer filtering
    const divisionName = useCallback((sid) => {
        return BLEEP_SERVER_NAMES[sid] || sid;
    }, []);

    const filteredCurrentAccounts = useMemo(() => {
        if (!currentRows) return [];
        const q = currentQuery.trim().toLowerCase();

        return currentRows.filter((r) => {
            const notes = Array.isArray(r.notes) ? r.notes : [];
            const matchesServer = currentAllDivisions || r.serverID === serverID;
            const matchesTest = currentShowTests || r.testAccount !== true;
            const matchesNotes = !currentWithNotesOnly || notes.length > 0;

            if (!matchesServer || !matchesTest || !matchesNotes) return false;

            if (q) {
                const div = divisionName(r.serverID).toLowerCase();
                const name = String(r.name || '').toLowerCase();
                const username = String(r.username || '').toLowerCase();
                const docId = String(r.documentID || '').toLowerCase();
                const searchable = `${name} ${username} ${docId} ${div}`;
                if (!searchable.includes(q)) return false;
            }

            return true;
        });
    }, [currentRows, currentQuery, currentAllDivisions, serverID, currentShowTests, currentWithNotesOnly, divisionName]);

    const currentTotalPages = Math.ceil(filteredCurrentAccounts.length / CURRENT_PAGE_SIZE) || 1;
    const paginatedCurrentAccounts = useMemo(() => {
        const start = currentPage * CURRENT_PAGE_SIZE;
        return filteredCurrentAccounts.slice(start, start + CURRENT_PAGE_SIZE);
    }, [filteredCurrentAccounts, currentPage]);

    // Export Official Licence CSV (matching bleep_monthly_licence_csv.dart)
    const handleExportLicenceCsv = () => {
        if (!report) return;

        const period = report.period || {};
        const heading = [
            'Period start',
            'Period end (excluded)',
            'Scope',
            'Report state',
            'Record type',
            'Name / metric',
            'Roles',
            'Divisions',
            'Count',
            'Event time',
            'Recorded creator',
        ];

        const csvRows = [heading];

        const addRow = (kind, name, { roles = '', divisions = '', count = '', at = '', actor = '' } = {}) => {
            csvRows.push([
                period.startDate || '',
                period.endDateExclusive || '',
                report.scope || (combined ? 'combined' : serverID),
                report.state || 'COMPLETE',
                kind,
                name,
                roles,
                divisions,
                count,
                at,
                actor,
            ]);
        };

        // Summary Lines
        addRow('SUMMARY', 'Monthly chargeable accounts', { count: report.chargeableAccounts ?? 'Not established' });
        addRow('SUMMARY', 'Confirmed active accounts in recorded history', { count: report.confirmedActiveAccounts ?? 0 });

        if (combined) {
            addRow('SUMMARY', 'Included contract baseline', { count: 350 });
            addRow('SUMMARY', 'Above baseline billable', { count: report.aboveBaseline ?? 0 });
        }

        // Individual Accounts (Single-Human Deduplication: 1 row per unique individual)
        accountRows.forEach((acc) => {
            const rolesStr = Array.isArray(acc.roles) ? acc.roles.join(' / ') : String(acc.roles || '');
            const divStr = Array.isArray(acc.divisions) ? acc.divisions.join(' / ') : String(acc.divisions || '');
            addRow('ACCOUNT', acc.name || 'Unnamed', {
                roles: rolesStr,
                divisions: divStr,
                count: 1,
            });
        });

        // Changes & Audit Events
        rows.filter((r) => r.kind === 'CHANGE').forEach((c) => {
            addRow(c.changeKind || 'CHANGE', c.name || '', {
                at: c.at || '',
                actor: c.recordedActor || 'Not recorded',
            });
        });

        // Add UTF-8 BOM (\uFEFF) for Excel compatibility
        const csvContent = '\uFEFF' + csvRows.map((r) => r.map(sanitizeCsvCell).join(',')).join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const scopeName = combined ? 'combined' : serverID;
        a.href = url;
        a.download = `monthly_licence_report_${scopeName}_${report.checksum || 'current'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            {/* Header: Title & Super Admin Scope Switcher */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #edf2f7',
            }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaFileInvoiceDollar style={{ fontSize: '20px', color: '#176B64' }} />
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                            Monthly Licence & Account Audit
                        </h3>
                    </div>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#718096' }}>
                        Immutable audit report of billable employee accounts based on live activity and captured baselines.
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {/* Super Admin "Plus Factor": Combined All 4 Franchises Switch */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: combined ? '#E5F2EF' : '#f8fafc',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${combined ? '#176B64' : '#cbd5e0'}`,
                        transition: 'all 0.15s ease',
                    }}>
                        <FaLayerGroup style={{ color: combined ? '#176B64' : '#a0aec0', fontSize: '14px' }} />
                        <label style={{ fontSize: '13px', fontWeight: '700', color: combined ? '#176B64' : '#4a5568', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={combined}
                                onChange={(e) => setCombined(e.target.checked)}
                                style={{ marginRight: '6px', cursor: 'pointer' }}
                            />
                            Combined All 4 Franchises
                        </label>
                    </div>

                    <button
                        type="button"
                        onClick={() => loadData(false)}
                        disabled={busy}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#edf2f7',
                            color: '#2b6cb0',
                            border: '1px solid #cbd5e0',
                            padding: '8px 14px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: busy ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <FaSync className={busy ? 'fa-spin' : ''} />
                        <span>Refresh</span>
                    </button>

                    <button
                        type="button"
                        onClick={handleExportLicenceCsv}
                        disabled={busy || !report}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#176B64',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '700',
                            cursor: busy || !report ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 4px rgba(23, 107, 100, 0.25)',
                        }}
                    >
                        <FaDownload />
                        <span>Export Licence CSV</span>
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#fff5f5',
                    color: '#c53030',
                    border: '1px solid #fed7d7',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                }}>
                    <FaExclamationTriangle />
                    <span>{error}</span>
                </div>
            )}

            {/* Loading Spinner */}
            {busy ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>
                    <FaSpinner className="fa-spin" style={{ fontSize: '28px', color: '#176B64', marginBottom: '12px' }} />
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
                        {initializing ? 'Initializing licence history baseline…' : 'Calculating monthly licence audit…'}
                    </p>
                </div>
            ) : report ? (
                <div>
                    {/* Metric Cards Row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '16px',
                        marginBottom: '20px',
                    }}>
                        {/* Chargeable Accounts Card */}
                        <div style={{
                            backgroundColor: '#F0F9F8',
                            border: '1px solid #BFE3DE',
                            borderRadius: '10px',
                            padding: '18px 20px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#176B64', marginBottom: '8px' }}>
                                <FaUsers />
                                <span style={{ fontSize: '13px', fontWeight: '700' }}>Chargeable Accounts</span>
                            </div>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#176B64' }}>
                                {report.chargeableAccounts ?? '—'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#657778', marginTop: '4px' }}>
                                {combined
                                    ? 'Single-human deduplicated across all 4 Bleep franchises'
                                    : `Active billable accounts on ${BLEEP_SERVER_NAMES[serverID] || serverID}`}
                            </div>
                        </div>

                        {/* Confirmed Active in History */}
                        <div style={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            padding: '18px 20px',
                        }}>
                            <div style={{ color: '#4a5568', fontSize: '13px', fontWeight: '700', marginBottom: '8px' }}>
                                Active in History
                            </div>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#2d3748' }}>
                                {report.confirmedActiveAccounts ?? accountRows.length}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                Verified by baseline + change events
                            </div>
                        </div>

                        {/* Combined Baseline / Above Baseline (for combined scope) */}
                        {combined && (
                            <>
                                <div style={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '10px',
                                    padding: '18px 20px',
                                }}>
                                    <div style={{ color: '#4a5568', fontSize: '13px', fontWeight: '700', marginBottom: '8px' }}>
                                        Contract Baseline
                                    </div>
                                    <div style={{ fontSize: '32px', fontWeight: '800', color: '#2d3748' }}>
                                        350
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                        Included monthly licence tier
                                    </div>
                                </div>

                                <div style={{
                                    backgroundColor: (report.aboveBaseline ?? 0) > 0 ? '#fff5f5' : '#ffffff',
                                    border: `1px solid ${(report.aboveBaseline ?? 0) > 0 ? '#feb2b2' : '#e2e8f0'}`,
                                    borderRadius: '10px',
                                    padding: '18px 20px',
                                }}>
                                    <div style={{
                                        color: (report.aboveBaseline ?? 0) > 0 ? '#c53030' : '#4a5568',
                                        fontSize: '13px',
                                        fontWeight: '700',
                                        marginBottom: '8px',
                                    }}>
                                        Above Baseline
                                    </div>
                                    <div style={{
                                        fontSize: '32px',
                                        fontWeight: '800',
                                        color: (report.aboveBaseline ?? 0) > 0 ? '#c53030' : '#2d3748',
                                    }}>
                                        {report.aboveBaseline ?? Math.max(0, (report.chargeableAccounts ?? 0) - 350)}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                        Additional billable user seats
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Immutable Report Checksum & Audit Tags */}
                    <div style={{
                        padding: '10px 14px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #edf2f7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                        fontSize: '12px',
                        color: '#4a5568',
                        marginBottom: '20px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaCheckCircle style={{ color: '#176B64' }} />
                            <span>Immutable Report Checksum: </span>
                            <code style={{ backgroundColor: '#edf2f7', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>
                                {(report.checksum || '').slice(0, 16)}…
                            </code>
                            {report.revision && (
                                <span style={{ backgroundColor: '#e2e8f0', padding: '1px 6px', borderRadius: '4px', fontWeight: '600' }}>
                                    Rev {report.revision}
                                </span>
                            )}
                        </div>
                        <div>
                            <span>Scope: <strong>{combined ? 'Combined 4 Franchises' : BLEEP_SERVER_NAMES[serverID]}</strong></span>
                        </div>
                    </div>

                    {/* Data Issues Warning (if any) */}
                    {dataIssues.length > 0 && (
                        <div style={{
                            backgroundColor: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderRadius: '8px',
                            padding: '14px 18px',
                            marginBottom: '20px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#b45309', fontWeight: '700', fontSize: '13px' }}>
                                <FaExclamationTriangle />
                                <span>{dataIssues.length} Data Issues Identified</span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#92400e', marginTop: '6px' }}>
                                Some records have unresolved statuses or missing authentication links that require administrative review.
                            </div>
                        </div>
                    )}

                    {/* Expandable Report Coverage Details (matching bleep_monthly_licence_panel.dart:763-828) */}
                    <div style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        marginBottom: '20px',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                    }}>
                        <div
                            onClick={() => setOpenCoverageDetails((prev) => !prev)}
                            style={{
                                padding: '14px 18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                backgroundColor: openCoverageDetails ? '#f8fafc' : '#ffffff',
                                borderBottom: openCoverageDetails ? '1px solid #e2e8f0' : 'none',
                                userSelect: 'none',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <FaClipboardCheck style={{ color: '#176B64', fontSize: '18px' }} />
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#2d3748' }}>
                                        Report details
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                                        Recorded evidence and tracking coverage
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#718096' }}>
                                {coverageGaps.length > 0 && (
                                    <span style={{
                                        backgroundColor: '#fff5f5',
                                        color: '#c53030',
                                        border: '1px solid #feb2b2',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                    }}>
                                        {coverageGaps.length} Incomplete
                                    </span>
                                )}
                                {openCoverageDetails ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </div>

                        {openCoverageDetails && (
                            <div style={{ padding: '18px 20px', backgroundColor: '#ffffff' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '13px' }}>
                                        <span style={{ color: '#64748b' }}>Accounts supported by available evidence:</span>
                                        <strong style={{ color: '#2d3748' }}>{report.confirmedActiveAccounts ?? 'Not recorded'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '13px' }}>
                                        <span style={{ color: '#64748b' }}>Recorded account changes:</span>
                                        <strong style={{ color: '#2d3748' }}>{report.changeRowCount ?? 0}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '13px' }}>
                                        <span style={{ color: '#64748b' }}>Report checks, including history gaps:</span>
                                        <strong style={{ color: '#2d3748' }}>{report.issueCount ?? (dataIssues.length || 0)}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '13px' }}>
                                        <span style={{ color: '#64748b' }}>Divisions with incomplete history:</span>
                                        <strong style={{ color: coverageGaps.length > 0 ? '#c53030' : '#176B64' }}>{coverageGaps.length}</strong>
                                    </div>
                                </div>

                                {coverageList.length > 0 && (
                                    <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '14px', marginBottom: '14px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                            Division Tracking Coverage
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px' }}>
                                            {coverageList.map((cov, cIdx) => (
                                                <div key={cov.serverID || cIdx} style={{
                                                    padding: '10px 14px',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${cov.complete !== true ? '#fed7d7' : '#e2e8f0'}`,
                                                    backgroundColor: cov.complete !== true ? '#fffaf0' : '#f8fafc',
                                                    fontSize: '12px',
                                                }}>
                                                    <div style={{ fontWeight: '700', color: '#2d3748', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <span>{divisionName(cov.serverID)}</span>
                                                        {cov.complete !== true ? (
                                                            <span style={{ color: '#c53030', fontWeight: '600', fontSize: '11px' }}>Incomplete</span>
                                                        ) : (
                                                            <span style={{ color: '#176B64', fontWeight: '600', fontSize: '11px' }}>Complete</span>
                                                        )}
                                                    </div>
                                                    <div style={{ color: '#64748b', marginTop: '4px' }}>
                                                        Tracking began {cov.recordedFrom ? new Date(cov.recordedFrom).toLocaleDateString() : 'Recorded start'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.6, borderTop: '1px solid #edf2f7', paddingTop: '12px' }}>
                                    <p style={{ margin: '0 0 6px 0' }}>
                                        Report checks are not a count of employees with incorrect IDs. Recorded additions may include migrated accounts.
                                    </p>
                                    {!isReportComplete && (
                                        <p style={{ margin: 0, color: '#b45309' }}>
                                            For incomplete periods, zero means no qualifying evidence was found in this report. It does not prove that no accounts or changes existed.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Search & Role Filter Bar for Accounts */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                        marginBottom: '14px',
                    }}>
                        <div style={{ position: 'relative', width: '320px', maxWidth: '100%' }}>
                            <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                                placeholder="Search account name or division…"
                                style={{
                                    width: '100%',
                                    padding: '8px 12px 8px 34px',
                                    fontSize: '13px',
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e0',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#718096', fontWeight: '600' }}>Role:</span>
                            {['ALL', 'employees', 'admins'].map((r) => (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => { setRoleFilter(r); setPage(0); }}
                                    style={{
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        border: `1px solid ${roleFilter === r ? '#176B64' : '#e2e8f0'}`,
                                        backgroundColor: roleFilter === r ? '#E5F2EF' : '#ffffff',
                                        color: roleFilter === r ? '#176B64' : '#4a5568',
                                    }}
                                >
                                    {r === 'ALL' ? 'All Roles' : r}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Deduplicated Accounts Table */}
                    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700' }}>Account Name</th>
                                    <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700' }}>Roles</th>
                                    <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700' }}>
                                        Franchises / Divisions {combined && <span style={{ color: '#176B64' }}>(Deduplicated)</span>}
                                    </th>
                                    <th style={{ padding: '10px 14px', color: '#4a5568', fontWeight: '700', textAlign: 'center' }}>Billable Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedAccounts.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: '#a0aec0' }}>
                                            No accounts match your search filters.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedAccounts.map((acc, idx) => {
                                        const divisions = Array.isArray(acc.divisions) ? acc.divisions : [acc.serverID].filter(Boolean);
                                        const roles = Array.isArray(acc.roles) ? acc.roles : ['employees'];

                                        return (
                                            <tr key={acc.accountKey || idx} style={{ borderBottom: '1px solid #edf2f7' }}>
                                                <td style={{ padding: '10px 14px', fontWeight: '600', color: '#2d3748' }}>
                                                    {acc.name || 'Name unavailable'}
                                                </td>
                                                <td style={{ padding: '10px 14px', color: '#718096' }}>
                                                    {roles.map((r) => (
                                                        <span key={r} style={{
                                                            backgroundColor: '#edf2f7',
                                                            color: '#4a5568',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontSize: '11px',
                                                            marginRight: '4px',
                                                        }}>
                                                            {r}
                                                        </span>
                                                    ))}
                                                </td>
                                                <td style={{ padding: '10px 14px' }}>
                                                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                        {divisions.map((d) => (
                                                            <span key={d} style={{
                                                                backgroundColor: '#E5F2EF',
                                                                color: '#176B64',
                                                                border: '1px solid #BFE3DE',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                            }}>
                                                                <FaBuilding style={{ marginRight: '4px', fontSize: '10px' }} />
                                                                {BLEEP_SERVER_NAMES[d] || d}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: '700', color: '#176B64' }}>
                                                    1
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Bar */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '12px',
                        fontSize: '12px',
                        color: '#718096',
                    }}>
                        <span>
                            Showing {filteredAccounts.length === 0 ? 0 : page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredAccounts.length)} of {filteredAccounts.length} billable accounts
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={page === 0}
                                style={{
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    border: '1px solid #cbd5e0',
                                    background: page === 0 ? '#f7fafc' : '#ffffff',
                                    color: page === 0 ? '#a0aec0' : '#4a5568',
                                    cursor: page === 0 ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Previous
                            </button>
                            <span>{page + 1} / {totalPages}</span>
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                disabled={page >= totalPages - 1}
                                style={{
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    border: '1px solid #cbd5e0',
                                    background: page >= totalPages - 1 ? '#f7fafc' : '#ffffff',
                                    color: page >= totalPages - 1 ? '#a0aec0' : '#4a5568',
                                    cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    {/* Expandable Current Accounts Explorer (matching bleep_current_accounts.dart) */}
                    <div style={{
                        marginTop: '24px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                    }}>
                        <div
                            onClick={() => {
                                const nextOpen = !openCurrentAccounts;
                                setOpenCurrentAccounts(nextOpen);
                                if (nextOpen && !currentLoaded && !currentBusy) {
                                    loadCurrentAccounts();
                                }
                            }}
                            style={{
                                padding: '16px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                backgroundColor: openCurrentAccounts ? '#f8fafc' : '#ffffff',
                                borderBottom: openCurrentAccounts ? '1px solid #e2e8f0' : 'none',
                                userSelect: 'none',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <FaUsers style={{ color: '#176B64', fontSize: '20px' }} />
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '15px', color: '#2d3748' }}>
                                        Current accounts
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                        Names, divisions and status today — separate from the selected report
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#718096' }}>
                                {currentRows && (
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#176B64', backgroundColor: '#E5F2EF', padding: '2px 8px', borderRadius: '10px' }}>
                                        {filteredCurrentAccounts.length} live records
                                    </span>
                                )}
                                {openCurrentAccounts ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                        </div>

                        {openCurrentAccounts && (
                            <div style={{ padding: '20px', backgroundColor: '#ffffff' }}>
                                {/* Explanatory Context Banners matching Flutter reference */}
                                <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '12.5px',
                                    color: '#64748b',
                                    lineHeight: 1.6,
                                    marginBottom: '16px',
                                }}>
                                    <p style={{ margin: '0 0 6px 0', fontWeight: '500' }}>
                                        Each row is a record in a division. A shared account may appear more than once. This list is not a chargeable licence total. Missing login IDs do not hide accounts.
                                    </p>
                                    <p style={{ margin: 0 }}>
                                        Shows employee and admin accounts with their status today, regardless of the reporting period selected above. Accounts load when you first expand this section. Use <em>Refresh accounts</em> to check for updates. This list does not change the monthly activity report or licence totals.
                                    </p>
                                </div>

                                {/* Controls: Search and Filter Chips */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
                                        <input
                                            type="text"
                                            value={currentQuery}
                                            onChange={(e) => { setCurrentQuery(e.target.value); setCurrentPage(0); }}
                                            placeholder="Search name, username, document ID, or division…"
                                            style={{
                                                width: '100%',
                                                padding: '9px 12px 9px 36px',
                                                fontSize: '13px',
                                                borderRadius: '8px',
                                                border: '1px solid #cbd5e0',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                            <button
                                                type="button"
                                                onClick={() => { setCurrentAllDivisions((v) => !v); setCurrentPage(0); }}
                                                style={{
                                                    padding: '5px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    border: `1px solid ${currentAllDivisions ? '#176B64' : '#cbd5e0'}`,
                                                    backgroundColor: currentAllDivisions ? '#E5F2EF' : '#ffffff',
                                                    color: currentAllDivisions ? '#176B64' : '#4a5568',
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                All four divisions
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => { setCurrentWithNotesOnly((v) => !v); setCurrentPage(0); }}
                                                style={{
                                                    padding: '5px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    border: `1px solid ${currentWithNotesOnly ? '#b45309' : '#cbd5e0'}`,
                                                    backgroundColor: currentWithNotesOnly ? '#fffbeb' : '#ffffff',
                                                    color: currentWithNotesOnly ? '#b45309' : '#4a5568',
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                With review notes
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => { setCurrentShowTests((v) => !v); setCurrentPage(0); }}
                                                style={{
                                                    padding: '5px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    border: `1px solid ${currentShowTests ? '#2b6cb0' : '#cbd5e0'}`,
                                                    backgroundColor: currentShowTests ? '#ebf8ff' : '#ffffff',
                                                    color: currentShowTests ? '#2b6cb0' : '#4a5568',
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                Show confirmed test accounts
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={loadCurrentAccounts}
                                            disabled={currentBusy}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                backgroundColor: '#edf2f7',
                                                color: '#2b6cb0',
                                                border: '1px solid #cbd5e0',
                                                padding: '6px 14px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                cursor: currentBusy ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            <FaSync className={currentBusy ? 'fa-spin' : ''} />
                                            <span>Refresh accounts</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Busy Indicator */}
                                {currentBusy && (
                                    <div style={{ padding: '30px', textAlign: 'center', color: '#718096' }}>
                                        <FaSpinner className="fa-spin" style={{ fontSize: '24px', color: '#176B64', marginBottom: '8px' }} />
                                        <div style={{ fontSize: '13px', fontWeight: '600' }}>Reading current accounts across all 4 divisions…</div>
                                    </div>
                                )}

                                {/* Error alert */}
                                {currentError && (
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#fff5f5',
                                        color: '#c53030',
                                        border: '1px solid #fed7d7',
                                        borderRadius: '8px',
                                        marginBottom: '16px',
                                        fontSize: '13px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                    }}>
                                        <FaExclamationTriangle />
                                        <span>{currentError}</span>
                                    </div>
                                )}

                                {/* Results display */}
                                {!currentBusy && currentRows && (
                                    <div>
                                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px', fontWeight: '600' }}>
                                            {filteredCurrentAccounts.length} source records
                                        </div>

                                        {filteredCurrentAccounts.length === 0 ? (
                                            <div style={{ padding: '30px', textAlign: 'center', color: '#a0aec0', fontSize: '13px', border: '1px dashed #e2e8f0', borderRadius: '8px' }}>
                                                No records match these filters.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {paginatedCurrentAccounts.map((r, rIdx) => {
                                                    const notes = Array.isArray(r.notes) ? r.notes : [];
                                                    return (
                                                        <div
                                                            key={r.source || `${r.serverID}_${r.documentID}_${rIdx}`}
                                                            style={{
                                                                padding: '14px 16px',
                                                                borderRadius: '8px',
                                                                border: '1px solid #e2e8f0',
                                                                backgroundColor: '#ffffff',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '6px',
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>
                                                                        {r.name || 'Name not recorded'}
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: '11px',
                                                                        fontWeight: '700',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        backgroundColor: r.statusLabel === 'Active' ? '#E5F2EF' : '#edf2f7',
                                                                        color: r.statusLabel === 'Active' ? '#176B64' : '#64748b',
                                                                    }}>
                                                                        {r.statusLabel || 'Unknown'}
                                                                    </span>
                                                                    {r.testAccount && (
                                                                        <span style={{
                                                                            fontSize: '11px',
                                                                            fontWeight: '600',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '12px',
                                                                            backgroundColor: '#fee2e2',
                                                                            color: '#991b1b',
                                                                        }}>
                                                                            Test account
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div style={{ fontSize: '12px', color: '#a0aec0', fontFamily: 'monospace' }}>
                                                                    {r.documentID}
                                                                </div>
                                                            </div>

                                                            <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                <span style={{ fontWeight: '600', color: '#4a5568' }}>{divisionName(r.serverID)}</span>
                                                                <span>·</span>
                                                                <span>{r.collection === 'admins' ? 'Admin' : 'Employee'}</span>
                                                                {r.username && (
                                                                    <>
                                                                        <span>·</span>
                                                                        <span style={{ fontFamily: 'monospace', color: '#2b6cb0' }}>@{r.username}</span>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {notes.length > 0 && (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                                                    {notes.map((note, nIdx) => (
                                                                        <div
                                                                            key={nIdx}
                                                                            style={{
                                                                                fontSize: '12px',
                                                                                color: '#92400e',
                                                                                backgroundColor: '#fef3c7',
                                                                                padding: '4px 8px',
                                                                                borderRadius: '4px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '6px',
                                                                                width: 'fit-content',
                                                                            }}
                                                                        >
                                                                            <FaExclamationTriangle style={{ fontSize: '11px' }} />
                                                                            <span>{note}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Pagination for Current Accounts */}
                                        {filteredCurrentAccounts.length > CURRENT_PAGE_SIZE && (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginTop: '16px',
                                                fontSize: '12px',
                                                color: '#718096',
                                            }}>
                                                <span>
                                                    Showing {currentPage * CURRENT_PAGE_SIZE + 1}–{Math.min((currentPage + 1) * CURRENT_PAGE_SIZE, filteredCurrentAccounts.length)} of {filteredCurrentAccounts.length} records
                                                </span>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                                                        disabled={currentPage === 0}
                                                        style={{
                                                            padding: '4px 10px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #cbd5e0',
                                                            background: currentPage === 0 ? '#f7fafc' : '#ffffff',
                                                            color: currentPage === 0 ? '#a0aec0' : '#4a5568',
                                                            cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                                                        }}
                                                    >
                                                        Previous
                                                    </button>
                                                    <span>{currentPage + 1} / {currentTotalPages}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentPage((p) => Math.min(currentTotalPages - 1, p + 1))}
                                                        disabled={currentPage >= currentTotalPages - 1}
                                                        style={{
                                                            padding: '4px 10px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #cbd5e0',
                                                            background: currentPage >= currentTotalPages - 1 ? '#f7fafc' : '#ffffff',
                                                            color: currentPage >= currentTotalPages - 1 ? '#a0aec0' : '#4a5568',
                                                            cursor: currentPage >= currentTotalPages - 1 ? 'not-allowed' : 'pointer',
                                                        }}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ padding: '30px', textAlign: 'center', color: '#a0aec0', fontSize: '14px' }}>
                    No licence report generated for this period. Click "Refresh" or "Initialize Baseline" to generate.
                </div>
            )}
        </div>
    );
};

export default MonthlyLicenceSection;
